import React from 'react';
import { Box, Typography, Radio } from '@mui/material';
import { gray40, gray80, gray100, grayBlue50, orange50 } from '../../../constants/colors';
import { boldSm, regularSm } from '../../../constants/location';
import { fileProcessingType } from '../../../constants/enum';
import { ReactComponent as PerPageIcon } from '../../assets/perPage.svg';
import { ReactComponent as PerFileIcon } from '../../assets/perFile.svg';

interface OutputTypeSelectorProps {
  value: number;
  onChange: (value: number) => void;
  showDescription?: boolean;
  maxWidth?: string;
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '24px',
    marginBottom: '24px',
  },
  label: {
    ...boldSm,
    color: grayBlue50,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  requiredBadge: {
    ...regularSm,
    color: orange50,
  },
  description: {
    ...regularSm,
    color: grayBlue50,
    textAlign: 'left',
    marginBottom: '16px',
  },
  cardsContainer: {
    display: 'flex',
    gap: '16px',
    '@media (max-width: 960px)': {
      flexDirection: 'column',
    },
  },
  card: {
    flex: '1 1 calc(50% - 8px)',
    maxWidth: '420px',
    border: `1px solid ${gray40}`,
    borderRadius: '8px',
    padding: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: orange50,
    },
    '@media (max-width: 960px)': {
      flex: 1,
      maxWidth: '100%',
    },
  },
  cardSelected: {
    border: `2px solid ${orange50}`,
    backgroundColor: 'rgba(255, 107, 53, 0.04)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  cardTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: gray100,
  },
  cardSubtitle: {
    fontSize: '12px',
    color: gray80,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
};

const OutputTypeSelector: React.FC<OutputTypeSelectorProps> = ({
  value,
  onChange,
  showDescription = true,
  maxWidth,
}) => {
  const isPagePerResult = value === fileProcessingType.pagePerResult;
  const isSingleResult = value === fileProcessingType.singleResult;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.label}>
        出力方式
        <Typography component="span" sx={styles.requiredBadge}>
          必須
        </Typography>
      </Typography>
      {showDescription && (
        <Typography sx={styles.description}>
          書類の形式に合った方法を選んでください。ファイルアップロード時に変更することも可能です。
        </Typography>
      )}

      <Box sx={{ ...styles.cardsContainer, ...(maxWidth && { maxWidth }) }}>
        {/* 1ページごとに出力 */}
        <Box
          sx={[styles.card, isPagePerResult && styles.cardSelected]}
          onClick={(): void => onChange(fileProcessingType.pagePerResult)}
        >
          <Box sx={styles.cardHeader}>
            <Radio
              checked={isPagePerResult}
              sx={{
                padding: 0,
                color: gray40,
                '&.Mui-checked': {
                  color: orange50,
                },
              }}
            />
            <Box sx={styles.cardContent}>
              <Box sx={styles.cardTitleContainer}>
                <Typography sx={styles.cardTitle}>1ページごとに出力</Typography>
                <Typography sx={styles.cardSubtitle}>（領収書・レシート向け）</Typography>
              </Box>
              <Box sx={styles.iconContainer}>
                <PerPageIcon />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 複数ページをまとめて出力 */}
        <Box
          sx={[styles.card, isSingleResult && styles.cardSelected]}
          onClick={(): void => onChange(fileProcessingType.singleResult)}
        >
          <Box sx={styles.cardHeader}>
            <Radio
              checked={isSingleResult}
              sx={{
                padding: 0,
                color: gray40,
                '&.Mui-checked': {
                  color: orange50,
                },
              }}
            />
            <Box sx={styles.cardContent}>
              <Box sx={styles.cardTitleContainer}>
                <Typography sx={styles.cardTitle}>複数ページをまとめて出力</Typography>
                <Typography sx={styles.cardSubtitle}>（契約書向け）</Typography>
              </Box>
              <Box sx={styles.iconContainer}>
                <PerFileIcon />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OutputTypeSelector;
